import { createFileRoute } from '@tanstack/react-router'
import { Building2, Crown, Ellipsis, KeyRound, LogOut, Plus, ShieldCheck, Trash2, Users } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { t } from '@/i18n'
import { Button, IconButton } from '@/ui/button'
import { AccountMenu } from '@/ui/account-menu'
import { Avatar, CompactRow, RowList } from '@/ui/compact-row'
import { DataList, type DataColumn } from '@/ui/data-list'
import { Field, PasswordField } from '@/ui/field'
import { InlineBanner } from '@/ui/inline-banner'
import { ConfirmDialog, RowMenu, RowMenuItem, SidePanel } from '@/ui/overlays'
import { EmptyState, PageHeader } from '@/ui/page'
import { SectionHeader } from '@/ui/section-header'
import { NeutralSegmented } from '@/ui/segmented'
import { SettingsRow, SettingsSection } from '@/ui/settings'
import { Spinner } from '@/ui/spinner'
import { Tab, Tabs } from '@/ui/tabs'
import { ThemeToggle } from '@/ui/theme-toggle'
import { Wordmark } from '@/ui/wordmark'

export const Route = createFileRoute('/dev/design')({
  component: DesignSystem,
})

/// /dev/design — every component of the panel, light and dark side by side,
/// the way the iOS components have previews in both themes. Nothing here
/// talks to a server.
function DesignSystem() {
  const [panel, setPanel] = useState(false)
  const [confirm, setConfirm] = useState(false)
  return (
    <div className="min-h-screen bg-bg">
      <div className="flex items-center justify-between px-10 pt-8">
        <div>
          <h1 className="text-title font-bold text-text">{t.design.title}</h1>
          <p className="text-callout text-text-2">{t.design.subtitle}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setPanel(true)}>
            Відкрити бічну панель
          </Button>
          <Button variant="secondary" onClick={() => setConfirm(true)}>
            Відкрити підтвердження
          </Button>
          <ThemeToggle />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-6 p-10 xl:grid-cols-2">
        <Specimen theme="light" />
        <Specimen theme="dark" />
      </div>

      <SidePanel
        open={panel}
        onOpenChange={setPanel}
        title="Бічна панель"
        description="Так на компʼютері виглядають аркуші з iOS: справа, з однією головною дією внизу."
        footer={
          <>
            <Button variant="quiet" onClick={() => setPanel(false)}>
              {t.app.cancel}
            </Button>
            <Button variant="primary">{t.app.save}</Button>
          </>
        }
      >
        <div className="flex flex-col gap-5">
          <Field label="Імʼя в компанії" placeholder="Як його бачитимуть колеги" />
          <InlineBanner kind="info" message="Підказка всередині панелі." />
        </div>
      </SidePanel>
      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title="Прибрати: Олена Коваль?"
        message="Людина одразу втратить доступ до компанії. Додати її знову можна будь-коли."
        confirmLabel="Прибрати"
        onConfirm={() => setConfirm(false)}
      />
    </div>
  )
}

/// Sample rows for the DataList specimen — the shape of «Продані».
type SoldSample = Readonly<{ id: string; client: string; plan: string; until: string; left: string; status: 'active' | 'frozen' | 'over' }>

const sold: readonly SoldSample[] = [
  { id: '1', client: 'Катерина Мельник', plan: 'Зал + басейн', until: '22 лист.', left: '8 з 8', status: 'active' },
  { id: '2', client: 'Дмитро Шевченко', plan: 'Безліміт на місяць', until: '22 лист.', left: '—', status: 'active' },
  { id: '3', client: 'Дарина Савченко', plan: '8 занять', until: '7 груд.', left: '3 з 8', status: 'frozen' },
  { id: '4', client: 'Роман Павленко', plan: '8 занять', until: '1 жовт.', left: '0 з 8', status: 'over' },
]

const statusLabel: Record<SoldSample['status'], string> = { active: 'Діє', frozen: 'Заморожено', over: 'Закінчився' }

const soldColumns: readonly DataColumn<SoldSample>[] = [
  {
    key: 'client',
    header: 'Клієнт',
    width: 'minmax(0,1.6fr)',
    cell: (row) => (
      <span className="flex min-w-0 items-center gap-2.5">
        <Avatar name={row.client} className="size-6 text-[11px] max-sm:hidden" />
        <span className="truncate">{row.client}</span>
      </span>
    ),
  },
  { key: 'plan', header: 'Тариф', width: 'minmax(0,1.2fr)', cell: (row) => row.plan },
  { key: 'until', header: 'До', width: '72px', numeric: true, cell: (row) => row.until, hideBelow: 'md' },
  { key: 'left', header: 'Залишок', width: '64px', numeric: true, cell: (row) => row.left },
  {
    key: 'status',
    header: 'Стан',
    width: '96px',
    align: 'end',
    mobile: 'trailing',
    cell: (row) => (
      <span className={row.status === 'active' ? 'font-medium text-success' : row.status === 'frozen' ? 'text-text-2' : 'text-muted'}>
        {statusLabel[row.status]}
      </span>
    ),
  },
]

const Block = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="flex flex-col gap-3">
    <h2 className="eyebrow">{title}</h2>
    {children}
  </section>
)

function Specimen({ theme }: { theme: 'light' | 'dark' }) {
  const [period, setPeriod] = useState<'own' | 'branches' | 'company'>('branches')
  const [checked, setChecked] = useState('front_desk')
  return (
    <div data-theme={theme} className="flex flex-col gap-8 rounded-[20px] bg-bg p-8 text-text ring-1 ring-black/5">
      <div className="flex items-center justify-between">
        <Wordmark className="h-6 w-auto text-text" />
        <span className="text-caption text-muted">{theme === 'light' ? t.theme.light : t.theme.dark}</span>
      </div>

      <Block title="Кольори">
        <div className="grid grid-cols-4 gap-2">
          {[
            ['bg', 'bg-bg ring-1 ring-surface-3'],
            ['surface', 'bg-surface'],
            ['surface2', 'bg-surface-2'],
            ['surface3', 'bg-surface-3'],
            ['accent', 'bg-accent'],
            ['success', 'bg-success'],
            ['warning', 'bg-warning'],
            ['danger', 'bg-danger'],
          ].map(([name, cls]) => (
            <div key={name} className="flex flex-col gap-1">
              <div className={`h-10 rounded-[10px] ${cls}`} />
              <span className="text-footnote text-muted">{name}</span>
            </div>
          ))}
        </div>
        <p className="text-callout">
          <span className="text-text">text</span> · <span className="text-text-2">text2</span> ·{' '}
          <span className="text-muted">muted</span> · <span className="text-accent-text">accentText</span>
        </p>
      </Block>

      <Block title="Шрифт · Golos Text">
        <div className="flex flex-col gap-1">
          <span className="text-display font-extrabold">Display 34</span>
          <span className="text-title font-bold">Title 28</span>
          <span className="text-heading font-bold">Heading 20</span>
          <span className="text-body">Body 17 — основний текст рядка</span>
          <span className="text-callout">Callout 15 — другорядний текст, кнопки</span>
          <span className="text-caption text-text-2">Caption 13 — мета, значення в щільних рядках</span>
          <span className="text-footnote text-muted">Footnote 12 — тихі підписи, найменший розмір</span>
          <span className="text-heading font-bold tabular">1 234 567 · 890 ₴</span>
        </div>
      </Block>

      <Block title="Кнопки">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="primary" icon={<Plus className="size-[18px]" strokeWidth={2.5} />}>
            Головна дія
          </Button>
          <Button variant="primary" busy>
            Зберігаємо…
          </Button>
          <Button variant="primary" disabled>
            Недоступно
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary">Другорядна</Button>
          <Button variant="quiet">Тиха</Button>
          <Button variant="danger" icon={<Trash2 className="size-4" />}>
            Видалити
          </Button>
          <Button variant="dangerFill">Прибрати</Button>
          <IconButton label="Ще">
            <Ellipsis className="size-5" />
          </IconButton>
          <ThemeToggle />
          <Spinner className="text-muted" />
        </div>
      </Block>

      <Block title="Поля">
        <div className="grid grid-cols-2 gap-4 [--well:var(--surface)]">
          <Field label="Пошта" placeholder="name@example.com" />
          <PasswordField label="Пароль" placeholder="Ваш пароль" defaultValue="secret-pass" />
          <Field label="Назва" defaultValue="Атлет" hint="Тиха підказка під полем." />
          <Field label="Знижка" defaultValue="120" error="Від 0 до 100" trailing={<span className="pr-4 text-muted">%</span>} />
        </div>
      </Block>

      <Block title="Повідомлення">
        <InlineBanner kind="error" message="Можна призначати лише тих, чия роль нижча за вашу" action={{ label: t.app.retry, onClick: () => {} }} />
        <InlineBanner kind="empty" title="Власних ролей ще немає" message="Скопіюйте системну роль або почніть з нуля." />
        <InlineBanner kind="stale" message="Показуємо дані з кешу" />
        <InlineBanner kind="info" message="Системна роль — лише для перегляду." />
        <InlineBanner kind="done" message="Компанію «Атлет» створено." action={{ label: 'Відкрити', onClick: () => {} }} />
      </Block>

      <Block title="Вибір · NeutralSegmented">
        <NeutralSegmented
          label="Охоплення"
          value={period}
          onChange={setPeriod}
          options={[
            { value: 'own', label: 'Лише своє' },
            { value: 'branches', label: 'Свої клуби' },
            { value: 'company', label: 'Уся компанія', disabled: true, hint: 'Вище за ваш рівень' },
          ]}
        />
      </Block>

      <Block title="Рядки · CompactRow">
        <RowList>
          <CompactRow leading={<Avatar name="Олена Коваль" />} title="Олена Коваль" subtitle="Оболонь, Поділ" value="Керуючий клубом" accessory="chevron" onClick={() => {}} />
          <CompactRow
            leading={<Avatar name="Іван Петренко" />}
            title="Іван Петренко"
            subtitle="Усі клуби"
            value="Тренер"
            actions={
              <RowMenu label="Дії" trigger={<Ellipsis className="size-5" />}>
                <RowMenuItem onSelect={() => {}}>Змінити роль</RowMenuItem>
                <RowMenuItem destructive onSelect={() => {}}>
                  Прибрати з компанії
                </RowMenuItem>
              </RowMenu>
            }
          />
          <CompactRow title="Адміністратор рецепції" subtitle="Свої клуби" accessory={checked === 'front_desk' ? 'check' : 'unchecked'} onClick={() => setChecked('front_desk')} />
          <CompactRow title="Тренер" subtitle="Лише своє" accessory={checked === 'trainer' ? 'check' : 'unchecked'} onClick={() => setChecked('trainer')} />
        </RowList>
      </Block>

      <Block title="Розділи · Tabs">
        <Tabs label="Абонементи">
          <Tab to="/dev/design" count="3">
            Тарифи
          </Tab>
          <Tab to="/dev/design/sold" count="65">
            Продані
          </Tab>
        </Tabs>
      </Block>

      <Block title="Заголовок групи · SectionHeader">
        <div className="flex flex-col gap-2">
          <SectionHeader title="У продажу" count="3" aside="Оновлено щойно" />
          <RowList>
            <CompactRow title="Зал + басейн" subtitle="Усі клуби · 30 днів" value="1 200 ₴" accessory="chevron" onClick={() => {}} />
          </RowList>
        </div>
      </Block>

      <Block title="Таблиця · DataList">
        <DataList
          label="Продані абонементи"
          columns={soldColumns}
          rows={sold}
          rowKey={(row) => row.id}
          rowTarget={() => ({ onClick: () => {} })}
          rowTone={(row) => (row.status === 'over' ? 'muted' : 'normal')}
          rowActions={() => (
            <RowMenu label="Дії" trigger={<Ellipsis className="size-[18px]" />}>
              <RowMenuItem onSelect={() => {}}>Заморозити</RowMenuItem>
              <RowMenuItem destructive onSelect={() => {}}>
                Анулювати
              </RowMenuItem>
            </RowMenu>
          )}
          footer={
            <>
              <span className="tabular">Показано 4 з 65</span>
              <Button compact variant="secondary">
                Показати ще
              </Button>
            </>
          }
        />
        <DataList label="Порожньо" columns={soldColumns} rows={[]} rowKey={(row) => row.id} empty="Проданих абонементів ще немає." />
      </Block>

      <Block title="Обліковий запис · AccountMenu">
        <div className="w-[232px]">
          <AccountMenu name="Олена Коваль" email="owner@local.test" onSignOut={() => {}} />
        </div>
      </Block>

      <Block title="Налаштування · SettingsSection">
        <SettingsSection title="Обліковий запис" meta="2" footer="Тиха примітка під групою.">
          <SettingsRow icon={<KeyRound className="size-[18px]" />} title="Двофакторний вхід" value="Увімкнено" onClick={() => {}} />
          <SettingsRow icon={<LogOut className="size-[18px]" />} title="Вийти" destructive onClick={() => {}} />
        </SettingsSection>
      </Block>

      <Block title="Навігація">
        <nav className="flex w-[232px] flex-col gap-px">
          <SectionHeader title="Компанія" inset={false} className="px-2.5 pb-1.5" />
          <span className="flex min-h-9 items-center gap-2.5 rounded-[10px] px-2.5 text-callout font-medium text-text tint-active">
            <Users className="size-[18px]" /> Співробітники
          </span>
          <span className="flex min-h-9 items-center gap-2.5 rounded-[10px] px-2.5 text-callout font-medium text-text-2">
            <ShieldCheck className="size-[18px] text-muted" /> Ролі
          </span>
          <SectionHeader title="Боги" inset={false} className="px-2.5 pt-5 pb-1.5" />
          <span className="flex min-h-9 items-center gap-2.5 rounded-[10px] px-2.5 text-callout font-medium text-text-2">
            <Building2 className="size-[18px] text-muted" /> Компанії
          </span>
          <span className="flex min-h-9 items-center gap-2.5 rounded-[10px] px-2.5 text-callout font-medium text-text-2">
            <Crown className="size-[18px] text-muted" /> Боги
          </span>
        </nav>
      </Block>

      <Block title="Заголовок сторінки">
        <div className="rounded-[16px] bg-surface px-6 pt-6">
          <PageHeader
            title="Абонементи"
            meta="3"
            subtitle="Тарифи, які продає клуб, і продані абонементи."
            actions={
              <>
                <Button variant="secondary">Експорт</Button>
                <Button variant="primary" icon={<Plus className="size-[18px]" strokeWidth={2.5} />}>
                  Новий тариф
                </Button>
              </>
            }
            tabs={
              <Tabs label="Абонементи">
                <Tab to="/dev/design">Тарифи</Tab>
                <Tab to="/dev/design/sold">Продані</Tab>
              </Tabs>
            }
          />
        </div>
      </Block>

      <Block title="Порожній стан">
        <div className="rounded-[16px] bg-surface">
          <EmptyState icon={<Users />} title="Тут ще нікого немає" message="Додайте людей за поштою їхнього акаунта eatlete." action={<Button variant="secondary">Додати</Button>} />
        </div>
      </Block>
    </div>
  )
}
